import type {
  ApiProjectSitesType,
  ApiReturnCompany,
  ApiReturnProject,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import CompanyService from '../../../api/services/config/company/CompanyService';
import ListDataGrid from '../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { useCheckPermission } from '../../../components/utils/permission/helpers/usePermission';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import { resetExternalState } from '../../../store/data/slice';
import { useAppDispatch } from '../../../store/hooks';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteService from '../../../api/services/config/site/SiteService';
import CreateRessource from '../../../components/ressources/CreateRessource';
import CreateSiteForm from '../../../components/sites/CreateSiteForm';
import EditSiteForm from '../../../components/sites/EditSiteForm';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import { getProjectFromSites } from '../../../store/config';
import type { DataState } from '../../../store/data/state';
import { getEntityById, getOrElse } from '../../../utils/functions';
import { useModal } from '../../../utils/hooks/useModal';
import { values } from '../../../utils/record';

type RowMUI = Partial<Omit<ApiReturnSite, 'id' | 'name'>> & {
  id: number;
  site: ApiReturnSite;
  name?: string;
  project?: string;
  exploitant?: string;
};

interface FilteredSitesProps {
  sites: ApiReturnSite[];
  projects: DataState<string, Record<number, ApiReturnProject>>;
  projectsSiteAssociation: DataState<
    string,
    Record<number, ApiProjectSitesType>
  >;
  refresh: () => void;
}

const FilteredSites: React.FC<FilteredSitesProps> = ({
  sites,
  projects,
  projectsSiteAssociation,
  refresh,
}) => {
  // use translation here
  const { t } = useTranslation('components.sites');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const [filteredSites] = useState(sites);
  const [companies, setCompanies] = useState<ApiReturnCompany[]>([]);
  const createModal = useModal();
  const { shouldHide: hideEditButton, canDisable: disableEditButton } =
    useCheckPermission('configuration_site_edit_button');
  const { shouldHide: hideDeleteButton, canDisable: disableDeleteButton } =
    useCheckPermission('configuration_site_delete_button');

  const editModal = useModal<{
    site: ApiReturnSite | null;
    projectSites: number[];
  }>();

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    projectId: number;
  }>();

  const addResourceModal = useModal<{
    project_id: number | null;
    site_id: number | null;
  }>();

  const deleteSite = async (id: number) => {
    submit({
      callbacks: [
        async () => await SiteService.delete(id),
        () => dispatch(resetExternalState({ external_key: 'sites' })),
        () => dispatch(resetExternalState({ external_key: 'project_sites' })),
        () => dispatch(resetExternalState({ external_key: 'site_devices' })),
      ],
      errorMessages: {
        anyError: t('Une erreur est survenue lors de la suppression du site'),
        unicityError: 'Vous avez déjà supprimé ce site',
      },
      successMessage: t('Le site a été supprimé avec succès'),
    });
  };

  const getProjectIdFromSiteId = (site_id: number): number => {
    return values(projectsSiteAssociation.data).find(
      (dt) => dt.site_id === site_id
    )?.project_id!;
  };

  const projectsSiteRecord = sites.reduce(
    (acc, site) => ({
      ...acc,
      [site.id]: getProjectFromSites(site, projectsSiteAssociation, projects),
    }),
    {} as Record<number, ApiReturnProject[]>
  );

  const defineProjectsLabel = (
    site: ApiReturnSite,
    projectRecord: Record<number, ApiReturnProject[]>
  ) => {
    const projectList = defineProjectList(site, projectRecord);
    if (projectList.length > 1) {
      return 'multiple';
    } else if (projectList.length === 0) {
      return null;
    } else {
      return (projectList[0] as ApiReturnProject).name;
    }
  };

  const defineProjectList = (
    site: ApiReturnSite,
    projectRecord: Record<number, ApiReturnProject[]>
  ) => {
    const projectList = projectRecord[site.id];
    if (projectList === undefined) {
      return [];
    } else {
      return projectList;
    }
  };

  const getCompanyName = (companyId: number | null | undefined) => {
    const company = companies.find((company) => company.id === companyId);
    return getOrElse(company?.name);
  };

  const headings: Record<string, string> = {
    project: t('Projet'),
    exploitant: t('Exploitant'),
  };

  const createRow = (site: ApiReturnSite): RowMUI => {
    const siteElem = getEntityById(site.id, sites);
    // console.log('siteElem', siteElem);
    // console.log('Project', getOrElse(defineProjectsLabel(site, projectsSiteRecord)));
    return {
      id: site.id,
      name: getOrElse(siteElem?.name),
      project: getOrElse(defineProjectsLabel(site, projectsSiteRecord)),
      exploitant: getCompanyName(site?.company_id),
      site,
    };
  };

  const rows: RowMUI[] = filteredSites.map(createRow).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  const createColumn = (field: keyof RowMUI): GridColDef<RowMUI> => ({
    field,
    headerName: headings[field] ?? field,
    minWidth: 100,
    sortable: true,
    filterable: true,
    headerClassName: 'list-data-grid-style_table-header',
    flex: 1,
  });

  const columns: GridColDef<RowMUI>[] = [
    {
      field: 'name',
      headerName: t('Nom'),
      sortable: true,
      filterable: true,
      minWidth: 150,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    ...(Object.keys(headings) as (keyof RowMUI)[]).map((key) =>
      createColumn(key)
    ),

    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      getActions: (
        params: import('@mui/x-data-grid-pro').GridRowParams<RowMUI>
      ) => {
        const row = params.row as RowMUI;
        const editEntry = !hideEditButton && (
          <GridActionsCellItem
            key={`update-${row.id}-${row.site.name}`}
            icon={<EditIcon />}
            label={t('Modifier')}
            data-testid={`edit-site-${row.site.name}`}
            onClick={() => {
              editModal.open({
                site: row.site,
                projectSites: Object.values(
                  defineProjectList(row.site, projectsSiteRecord)
                )
                  .flat()
                  .map((project) => project.id),
              });
            }}
            showInMenu
            disabled={disableEditButton}
          />
        );
        const deleteEntry = (
          !hideDeleteButton && (
            <GridActionsCellItem
              key={`delete-${row.id}-${row.site.postal_code}`}
              icon={<DeleteIcon />}
              label={t('Supprimer')}
              data-testid={`delete-site-${row.site.name}`}
              onClick={() => {
                deleteModal.open({
                  callback: () => deleteSite(row.site.id),
                  message: t('Voulez-vous supprimer le site'),
                  target: row.site.name,
                  projectId: getProjectIdFromSiteId(row.site.id),
                });
              }}
              showInMenu
              disabled={disableDeleteButton}
            />
          )
        );
        return [editEntry, deleteEntry].filter(Boolean) as React.ReactElement[];
      },
    },
  ];

  useEffect(() => {
    CompanyService.getCompanies()
      .then((companies) => {
        setCompanies(companies);
      })
      .catch((error) => {
        console.error('Error fetching companies:', error);
      });
  }, []);

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item={'configuration_site_create_button'}>
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
            data-testid="create-site"
          >
            {t('Créer un site')}
          </button>
        </PermissionDecoratorV2>
      </div>

      <ListDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        height="calc(100vh - 80px - 125px)"
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />

      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback()}
          open={deleteModal.isOpen}
          title={'Confirmation'}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}
      {createModal.isOpen && (
        <CreateSiteForm
          onCancel={createModal.close}
          isOpen={createModal.isOpen}
        />
      )}
      {editModal.isOpen && (
        <EditSiteForm
          site={editModal.params?.site!}
          isModalOpen={editModal.isOpen}
          onCancel={editModal.close}
          projectSites={editModal.params?.projectSites!}
        //open={editModal.isOpen}
        />
      )}
      {addResourceModal.isOpen && (
        <CreateRessource
          handleClose={addResourceModal.close}
          isModalOpen={addResourceModal.isOpen}
          project_id={addResourceModal.params?.project_id!}
          site_id={addResourceModal.params?.site_id!}
        />
      )}
    </div>
  );
};

export default FilteredSites;
